/**
 * Where a plank bridge goes over a gorge, as plain maths (no Three.js): the views place the kit's span models and
 * their timber posts from it, and the terrain answers the deck's height from it.
 *
 * The bridges used to be the kit's one-, two- or three-cell span dropped on the chasm cells at the bank's height. The
 * gorge as DRAWN is not those cells: the ground mesh wanders every vertex sideways, hardest at a gorge's lip (up to
 * ~0.6 of a unit, groundGeometry.ts), so a one-cell gorge is often 1.5–2 units across where the road meets it. The
 * one-unit span then reached neither lip and hung in the gap, and its legs stopped a metre above a 2.3-unit-deep floor
 * (Kevin, 2026-10-24, level 2 "The Ford"). Here the span is fitted to the gorge on screen instead:
 *
 * - the lips are found by walking the road's centre line over the drawn surface until it runs out (and back in);
 * - the deck runs from a seat on one bank to a seat on the other, past each lip by the length of its end abutment, so
 *   the abutments rest on the ground; it leaves each lip at the road surface there — sloped if the banks differ, so
 *   there is no step at either;
 * - the kit model and how many of them (a long gap takes two or three spans meeting over a joint post) are chosen so
 *   the planks are stretched as little as possible and no model leg reaches into a cliff wall;
 * - every leg (and every joint) gets a post down to what is under it: the gorge floor, or the wall's own batter.
 */

/** Drawn ground height at a world point, NaN over a hole (GroundMesh.sheetAt). */
export type SurfaceAt = (x: number, z: number) => number;
/** World y of the highest solid thing (gorge floor, cliff ledge, ground) under a point, looking down from `fromY`. */
export type SolidBelow = (x: number, z: number, fromY: number) => number;

/** A kit span model: its length in model units, where its legs stand (along, across) and how far down they reach. */
export interface SpanModel { readonly cells: 1 | 2 | 3; readonly legsAlong: readonly number[]; readonly legAcross: number; readonly legBottom: number }

/** The kit's `g_bridge_1/2/3`, measured from the models (deck top at the pivot, legs below; art/hq/anchors.json). */
export const SpanModels: readonly SpanModel[] = [
  { cells: 1, legsAlong: [0], legAcross: 0.4, legBottom: -0.86 },
  { cells: 2, legsAlong: [-0.5, 0.5], legAcross: 0.45, legBottom: -1.06 },
  { cells: 3, legsAlong: [-0.93, 0, 0.93], legAcross: 0.47, legBottom: -1.26 },
];

export const BridgeFitTuning = {
  /**
   * How far the deck runs onto each bank past the lip, so the end abutment rests on ground: the abutment is this
   * long in model units (stretched with the span), plus a margin, within limits; `Seat` is the first guess. At the
   * longest seat a fully stretched abutment's inner few centimetres overhang the lip, as a beam end would.
   */
  Seat: 0.3,
  Abutment: 0.2,
  SeatMargin: 0.04,
  MinSeat: 0.2,
  MaxSeat: 0.36,
  SeatPasses: 4,
  /**
   * Passes that lower a deck end where its seat would stand proud of a bank falling away past the lip, and how proud
   * it may stand: the end abutment reaches 0.59 below the deck, so up to this much it still stands on the ground.
   */
  SettlePasses: 10,
  SeatFloat: 0.3,
  /** Let down a little past the limit, so the passes settle where the seat is sampled between steps too. */
  SettleMargin: 0.02,
  /** The span models may be stretched or squeezed along their length this far, no further. */
  MinStretch: 0.7,
  MaxStretch: 2,
  /** Step of the lip search along the road's centre line, in world units, and its refinement passes. */
  Step: 1 / 16,
  Refine: 6,
  /** How deep the planks are under the deck top: rock is looked for from here down. */
  DeckDepth: 0.3,
  /** Joint between two spans: the abutments meeting there reach this far below the deck (model units). */
  JointBottom: -0.59,
  /** Posts start this far up inside the leg they continue and go this far into what they stand on. */
  PostOverlap: 0.06,
  PostEmbed: 0.05,
  /** How far behind the bank cell's centre (and past the far one's) the lips are looked for, in world units. */
  Behind: 0.75,
  /** Most spans one crossing is split into. */
  MaxSegments: 3,
} as const;

/** One kit model in a fitted bridge: which one, where its pivot is (the deck top's middle), its length and stretch. */
export interface SpanPiece { readonly model: SpanModel; readonly x: number; readonly y: number; readonly z: number; readonly length: number; readonly stretch: number }
/** A timber post from a leg (or a joint) down to the ground under it. */
export interface BridgePost { readonly x: number; readonly z: number; readonly top: number; readonly bottom: number }

/** A deck fitted to a crossing: a straight line from seat A to seat B (road surface at both lips), its pieces and posts. */
export interface BridgeFit {
  readonly ax: number; readonly az: number; readonly ay: number;
  readonly bx: number; readonly bz: number; readonly by: number;
  /** Unit direction from A to B on the ground plane, and the horizontal length A→B. */
  readonly ux: number; readonly uz: number; readonly length: number;
  /** Distances from A along the deck where the gorge opens and where it closes again. */
  readonly lipA: number; readonly lipB: number;
  readonly pieces: readonly SpanPiece[];
  readonly posts: readonly BridgePost[];
  /** Pitch of the deck, in radians: positive rises toward B. */
  readonly pitch: number;
}

/** The crossing to fit: the centre of the bank cell the road leaves from, the grid direction into the gorge and how many chasm cells the road crosses. */
export interface Crossing { readonly x: number; readonly z: number; readonly dx: number; readonly dz: number; readonly cells: number }

/** Deck height of a fitted bridge at a world point, or NaN off its footprint (half a cell either side of its line). */
export function deckHeightAt(f: BridgeFit, x: number, z: number): number {
  const along = (x - f.ax) * f.ux + (z - f.az) * f.uz;
  const across = -(x - f.ax) * f.uz + (z - f.az) * f.ux;
  if (along < 0 || along > f.length || Math.abs(across) > 0.5) return NaN;
  return f.ay + (f.by - f.ay) * (along / f.length);
}

/**
 * The distance along (x, z) + t·(dx, dz) at which `solid(t)` changes from `from` to the other answer, searching
 * [t0, t1] in `Step`s and bisecting the step it changes in. Null if it never changes.
 */
function edgeAlong(solid: (t: number) => boolean, from: boolean, t0: number, t1: number): number | null {
  const S = BridgeFitTuning.Step;
  for (let t = t0; t < t1; t += S) {
    const b = Math.min(t1, t + S);
    if (solid(b) === from) continue;
    let lo = t, hi = b;
    for (let k = 0; k < BridgeFitTuning.Refine; k++) { const m = (lo + hi) / 2; if (solid(m) === from) lo = m; else hi = m; }
    return lo;
  }
  return null;
}

/** Fits a bridge to a crossing on the drawn ground. */
export function fitBridge(c: Crossing, surface: SurfaceAt, below: SolidBelow): BridgeFit {
  const T = BridgeFitTuning;
  const at = (t: number): [number, number] => [c.x + c.dx * t, c.z + c.dz * t];
  const solid = (t: number) => { const [x, z] = at(t); return Number.isFinite(surface(x, z)); };
  // The far bank cell's centre is cells + 1 along. A lip can wander past a bank cell's centre, so the search runs from
  // a little behind the near one (from the last ground there: a bank can be a single row between two gorges) to a
  // little past the far one, and the near seat may not reach back past the start of that ground.
  const far = c.cells + 1, back = -T.Behind;
  let start = 0;
  while (start > back && !solid(start)) start -= T.Step;
  let bank = start;
  while (bank - T.Step > back && solid(bank - T.Step)) bank -= T.Step;
  const lipA = edgeAlong(solid, true, start, far + T.Behind) ?? 0.5;
  const lipB = (edgeAlong(solid, false, lipA + T.Step / 4, far + T.Behind) ?? far - 0.5) + T.Step / 2 ** T.Refine;
  const farGround = solid(lipB + T.Step);
  const ground = (t: number) => { const [x, z] = at(t); return surface(x, z); };
  // The seat each end needs is the stretched abutment's length, and the stretch depends on the seats: lengthen the
  // seats until they hold the abutments of the spans chosen for them.
  let seat: number = T.Seat;
  let fit = deckLine(seat);
  let choice = chooseSpans(fit.length, clearOf(fit), fit.sA);
  for (let k = 0; k < T.SeatPasses; k++) {
    const need = Math.min(T.MaxSeat, Math.max(T.MinSeat, T.Abutment * ((fit.length / choice.count) / choice.model.cells) + T.SeatMargin));
    if (need <= seat) break;
    seat = need;
    fit = deckLine(seat);
    choice = chooseSpans(fit.length, clearOf(fit), fit.sA);
  }
  const { sA, sB, length, line } = fit;
  const [ax, az] = at(sA), [bx, bz] = at(sB);
  const ay = line(sA), by = line(sB);
  const pitch = Math.atan2(by - ay, length);
  const pieces: SpanPiece[] = [];
  const posts: BridgePost[] = [];
  const seg = length / choice.count;
  for (let k = 0; k < choice.count; k++) {
    const mid = sA + seg * (k + 0.5);
    const [x, z] = at(mid);
    pieces.push({ model: choice.model, x, y: line(mid), z, length: seg, stretch: seg / choice.model.cells });
    for (const l of choice.model.legsAlong) addPosts(posts, mid + l * (seg / choice.model.cells), choice.model.legBottom, choice.model.legAcross);
    if (k > 0) addPosts(posts, sA + seg * k, T.JointBottom, choice.model.legAcross);
  }
  return { ax, az, ay, bx, bz, by, ux: c.dx, uz: c.dz, length, lipA: lipA - sA, lipB: lipB - sA, pieces, posts, pitch };

  /**
   * The deck's line for seats `seat` long: through the ground at both lips, so the planks leave each bank flush with
   * the road whatever the two banks' heights. A bank that rises past its lip takes the seat's end into it, under the
   * road; one that falls away leaves the end standing on its abutment, and past `SeatFloat` the end is lowered.
   */
  function deckLine(seat: number): { sA: number; sB: number; length: number; line: (t: number) => number } {
    const sA = Math.max(bank, lipA - seat), sB = Math.min(far + T.Behind, lipB + seat);
    const gap = Math.max(T.Step, lipB - lipA);
    let ya = ground(lipA - T.Step / 8);
    let yb = farGround ? ground(lipB + T.Step / 8) : ya;           // no far bank (the road ends here): a level deck
    if (!Number.isFinite(ya)) ya = Number.isFinite(yb) ? yb : below(...at(lipA), Infinity);
    if (!Number.isFinite(yb)) yb = ya;
    const line = (t: number) => ya + (yb - ya) * ((t - lipA) / gap);
    for (let k = 0; k < T.SettlePasses; k++) {
      // the end's height counts (1 + how far past the lip, over the gap) at a seat point: lower it by just enough
      let da = 0, db = 0;
      for (let t = sA; t <= lipA; t += T.Step) { const g = ground(t); if (Number.isFinite(g)) da = Math.max(da, (line(t) - g - T.SeatFloat + T.SettleMargin) / (1 + (lipA - t) / gap)); }
      if (farGround) for (let t = lipB; t <= sB; t += T.Step) { const g = ground(t); if (Number.isFinite(g)) db = Math.max(db, (line(t) - g - T.SeatFloat + T.SettleMargin) / (1 + (t - lipB) / gap)); }
      if (da <= T.SettleMargin && db <= T.SettleMargin) break;
      ya -= Math.max(0, da); yb -= Math.max(0, db);
    }
    return { sA, sB, length: sB - sA, line };
  }
  function clearOf(f: { line: (t: number) => number }): (t: number, bottom: number, across: number) => number {
    return (t, bottom, across) => {
      let worst = Infinity;
      for (const side of [-1, 1]) {
        const [x, z] = sideAt(t, across * side);
        // a leg under the bank is buried and hidden; one over the gorge must clear the rock it hangs above
        if (Number.isFinite(surface(x, z))) continue;
        worst = Math.min(worst, f.line(t) + bottom - below(x, z, f.line(t) - T.DeckDepth));
      }
      return worst;
    };
  }

  function sideAt(t: number, across: number): [number, number] { const [cx, cz] = at(t); return [cx - c.dz * across, cz + c.dx * across]; }
  function addPosts(out: BridgePost[], t: number, bottom: number, across: number): void {
    for (const side of [-1, 1]) {
      const [x, z] = sideAt(t, across * side);
      const top = line(t) + bottom + T.PostOverlap;
      const foot = below(x, z, line(t) + bottom);
      if (foot >= top - T.PostOverlap) continue;                 // the leg already stands on something: no post
      out.push({ x, z, top, bottom: foot - T.PostEmbed });
    }
  }
}

/**
 * How many spans of which model cover `length`: the least stretched choice whose legs all hang clear of the rock
 * (`clear(t, bottom, across)` is how far the legs `across` either side of the line, reaching `bottom` below the deck
 * at `t`, stand above the rock; negative means they stand in it). With no clean choice, the least buried one: its
 * legs then rest on a wall's ledge or batter.
 */
export function chooseSpans(length: number, clear: (t: number, bottom: number, across: number) => number, t0 = 0): { model: SpanModel; count: number } {
  const T = BridgeFitTuning;
  let best: { model: SpanModel; count: number; ok: boolean; cost: number; worst: number } | null = null;
  for (let count = 1; count <= T.MaxSegments; count++) {
    const seg = length / count;
    for (const model of SpanModels) {
      const stretch = seg / model.cells;
      if (stretch < T.MinStretch || stretch > T.MaxStretch) continue;
      let worst = Infinity;
      for (let k = 0; k < count; k++) {
        const mid = t0 + seg * (k + 0.5);
        for (const l of model.legsAlong) worst = Math.min(worst, clear(mid + l * stretch, model.legBottom, model.legAcross));
        if (k > 0) worst = Math.min(worst, clear(t0 + seg * k, T.JointBottom, model.legAcross));
      }
      const ok = worst >= 0;
      const cost = Math.abs(Math.log(stretch)) + 0.2 * (count - 1);
      const better = !best || (ok && !best.ok) || (ok === best.ok && (ok ? cost < best.cost : worst > best.worst));
      if (better) best = { model, count, ok, cost, worst };
    }
  }
  if (best) return { model: best.model, count: best.count };
  // nothing fits the stretch limits (a sliver of a gap, or one far too long): the nearest model, as many as it takes
  const count = Math.min(T.MaxSegments, Math.max(1, Math.ceil(length / (3 * T.MaxStretch))));
  const model = SpanModels[Math.min(2, Math.max(0, Math.round(length / count) - 1))];
  return { model, count };
}
